import React from 'react';
import { Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAddons } from '@/hooks/useProducts';
import { useCart } from '@/hooks/useCart';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { formatCurrency } from '@/lib/currency';
import { optimizeImageUrl } from '@/utils/image-optimizer';

interface CartAddonsStripProps {
  language?: 'en' | 'ar';
  /**
   * Lets the host page tune the layout. `drawer` (default) is the compact
   * horizontal scroll used in CartDrawer; `summary` is a slightly larger
   * grid for use in the checkout order-summary card.
   */
  variant?: 'drawer' | 'summary';
}

const COPY = {
  en: {
    title: 'Add to your order',
    subtitle: 'Small touches that make a big difference.',
    add: 'Add',
    added: 'Added',
    empty: '',
  },
  ar: {
    title: 'ضيف لطلبك',
    subtitle: 'لمسات صغيرة بتفرق كتير.',
    add: 'ضيف',
    added: 'تم',
    empty: '',
  },
} as const;

const CartAddonsStrip: React.FC<CartAddonsStripProps> = ({
  language = 'en',
  variant = 'drawer',
}) => {
  const { selectedCity } = useDeliveryLocation();
  const { data: addons, isLoading } = useAddons(selectedCity?.slug);
  const { state, addItem, getItemQuantity } = useCart();
  const t = COPY[language];
  const isRtl = language === 'ar';

  // Don't render any chrome if there's nothing useful to show.
  if (isLoading) return null;
  if (!addons || addons.length === 0) return null;

  const handleAdd = (addon: any) => {
    addItem({
      id: addon.id,
      name: language === 'ar' && addon.name_ar ? addon.name_ar : addon.name_en,
      price: Number(addon.price),
      image_url: addon.image_url,
      category: addon.categories?.slug,
    });
  };

  return (
    <div
      className={`rounded-lg border border-border bg-muted/30 p-3 ${isRtl ? 'text-right' : ''}`}
      dir={isRtl ? 'rtl' : 'ltr'}
      data-testid="cart-addons-strip"
    >
      <div className="mb-2">
        <p className="font-semibold text-sm">{t.title}</p>
        <p className="text-xs text-muted-foreground">{t.subtitle}</p>
      </div>

      {/* Horizontal scroller — keeps the cart compact even with 6+ addons. */}
      <div
        className={`flex gap-2 overflow-x-auto pb-1 ${variant === 'summary' ? 'gap-3' : ''}`}
        style={{ scrollbarWidth: 'thin' }}
      >
        {addons.map((addon: any) => {
          const isInCart = getItemQuantity(addon.id) > 0;
          const displayName =
            language === 'ar' && addon.name_ar ? addon.name_ar : addon.name_en;
          // Drop the noisy "(price per 1 piece)" suffix in the UI; the price
          // line already tells the customer this is per-item.
          const cleanName = displayName.replace(/\s*\(price per[^)]*\)/i, '').trim();

          return (
            <div
              key={addon.id}
              className={`flex-shrink-0 ${variant === 'summary' ? 'w-32' : 'w-28'} rounded-md bg-background border border-border p-2 flex flex-col`}
            >
              {addon.image_url ? (
                <img
                  src={optimizeImageUrl(addon.image_url, 200)}
                  alt={cleanName}
                  className={`w-full ${variant === 'summary' ? 'h-24' : 'h-20'} object-cover rounded mb-2`}
                  loading="lazy"
                />
              ) : (
                <div className={`w-full ${variant === 'summary' ? 'h-24' : 'h-20'} bg-muted rounded mb-2`} />
              )}
              <p className="text-xs font-medium line-clamp-2 mb-1" title={cleanName}>
                {cleanName}
              </p>
              <p className="text-xs text-muted-foreground mb-2 tabular-nums">
                {formatCurrency(Number(addon.price), { language })}
              </p>
              <Button
                size="sm"
                variant={isInCart ? 'secondary' : 'default'}
                className="h-7 text-xs mt-auto"
                onClick={() => handleAdd(addon)}
                aria-label={`${t.add} ${cleanName}`}
              >
                {isInCart ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    {t.added}
                  </>
                ) : (
                  <>
                    <Plus className="h-3 w-3 mr-1" />
                    {t.add}
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartAddonsStrip;
